"use client";

import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  Info,
  PhoneOff,
  Plus,
  ShieldCheck,
  XCircle,
} from "lucide-react";
import { useState } from "react";
import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { endpoint } from "@/app/_module/services/endpoints";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/app/_module/components/ui/dialog";
import { Button } from "@/app/_module/components/ui/button";
import { formatPhoneNumberDisplay } from "@/app/_module/utils/phoneUtils";

/** Shape returned by POST /stripe/verify-purchases (the `data` object). */
export type VerifyPurchasesData = {
  provisioned?: Array<{ areaCode: string; phoneNumber: string; sessionId?: string }>;
  alreadyExisted?: Array<{ areaCode: string; sessionId?: string }>;
  skippedUnpaid?: Array<{ areaCode: string; sessionId?: string }>;
  /** Numbers the user holds that no active subscription pays for. */
  unbilled?: Array<{ id: string; phoneNumber: string; areaCode: string }>;
  failed?: Array<{ areaCode: string; reason: string; sessionId?: string }>;
  duplicateSubscriptions?: unknown[];
  subscriptionFix?: { fixed?: boolean } | null;
  webhookHealth?: { reEnabled?: boolean } | null;
  summary?: string;
};

type Tone = "good" | "info" | "warn" | "bad";

const TONE: Record<Tone, { ring: string; chip: string; icon: string }> = {
  good: {
    ring: "border-emerald-200 dark:border-emerald-900",
    chip: "bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300",
    icon: "text-emerald-600 dark:text-emerald-400",
  },
  info: {
    ring: "border-gray-200 dark:border-gray-800",
    chip: "bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300",
    icon: "text-gray-500 dark:text-gray-400",
  },
  warn: {
    ring: "border-amber-200 dark:border-amber-900",
    chip: "bg-amber-50 text-amber-800 dark:bg-amber-950 dark:text-amber-300",
    icon: "text-amber-600 dark:text-amber-400",
  },
  bad: {
    ring: "border-red-200 dark:border-red-900",
    chip: "bg-red-50 text-red-700 dark:bg-red-950 dark:text-red-300",
    icon: "text-red-600 dark:text-red-400",
  },
};

/**
 * Collapsed by default for anything routine. A book of 70+ cancelled area codes
 * would otherwise bury the one line that matters (what actually got bought).
 */
function Section({
  tone,
  icon,
  title,
  count,
  blurb,
  items,
  defaultOpen = false,
}: {
  tone: Tone;
  icon: React.ReactNode;
  title: string;
  count: number;
  blurb: string;
  items: string[];
  defaultOpen?: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  if (count === 0) return null;
  const t = TONE[tone];

  return (
    <div className={`rounded-lg border ${t.ring}`}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-3 px-3 py-2.5 text-left"
      >
        <span className={t.icon}>{icon}</span>
        <span className="flex-1">
          <span className="block text-sm font-medium">{title}</span>
          <span className="block text-xs text-gray-500 dark:text-gray-400">
            {blurb}
          </span>
        </span>
        <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${t.chip}`}>
          {count}
        </span>
        <ChevronDown
          className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>

      {open && (
        <div className="max-h-56 overflow-y-auto overscroll-contain border-t border-gray-100 px-3 py-2 dark:border-gray-800">
          <ul className="space-y-1">
            {items.map((label, i) => (
              <li
                key={`${label}-${i}`}
                className="font-mono text-xs text-gray-600 dark:text-gray-300"
              >
                {label}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export function VerifyPurchasesResult({
  open,
  onOpenChange,
  data,
  error,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  data: VerifyPurchasesData | null;
  /** Set when the request itself failed, so the panel can say so plainly. */
  error?: string | null;
}) {
  const provisioned = data?.provisioned ?? [];
  const existed = data?.alreadyExisted ?? [];
  const skipped = data?.skippedUnpaid ?? [];
  const unbilled = data?.unbilled ?? [];
  const [busyId, setBusyId] = useState<string | null>(null);

  /**
   * Reuses the existing phone_number_recovery checkout. Because an unbilled
   * number still has its phoneSid, the webhook attaches a subscription and keeps
   * the SAME number — it only provisions a replacement when the sid is gone.
   */
  const keepNumber = async (n: { id: string; phoneNumber: string; areaCode: string }) => {
    setBusyId(n.id);
    try {
      const res: any = await api.post({
        url: getApiUrl(endpoint.stripe.checkoutSessions),
        body: {
          items: [
            {
              type: "phone_number_recovery",
              phoneNumberId: n.id,
              areaCode: n.areaCode,
              phoneNumber: n.phoneNumber,
            },
          ],
          successUrl: `${window.location.origin}/settings?tab=phone-numbers&status=recovery_success`,
          cancelUrl: `${window.location.origin}/settings?tab=phone-numbers&status=recovery_cancelled`,
        },
        auth: true,
      });
      const url = res?.data?.url ?? res?.url;
      if (url) window.location.href = url;
      else setBusyId(null);
    } catch {
      setBusyId(null);
    }
  };
  const failed = data?.failed ?? [];

  const nothingChanged =
    !error && provisioned.length === 0 && failed.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {error ? (
              <XCircle className="h-5 w-5 text-red-600" />
            ) : provisioned.length > 0 ? (
              <ShieldCheck className="h-5 w-5 text-emerald-600" />
            ) : (
              <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            )}
            {error
              ? "Verification failed"
              : provisioned.length > 0
                ? "Purchases restored"
                : "Everything is up to date"}
          </DialogTitle>
        </DialogHeader>

        {error ? (
          <p className="text-sm text-gray-600 dark:text-gray-300">{error}</p>
        ) : (
          <div className="space-y-3">
            {/* Plain-language headline before any of the detail. */}
            <p className="text-sm text-gray-600 dark:text-gray-300">
              {nothingChanged
                ? "We checked every past purchase against your live subscriptions. Nothing was missing, so no new numbers were bought."
                : `We restored ${provisioned.length} number${provisioned.length === 1 ? "" : "s"} that had been paid for but never set up.`}
            </p>

            <Section
              tone="good"
              icon={<Plus className="h-4 w-4" />}
              title="Numbers added"
              blurb="Paid for previously but never provisioned"
              count={provisioned.length}
              defaultOpen
              items={provisioned.map(
                (p) => `${formatPhoneNumberDisplay(p.phoneNumber)}  ·  area code ${p.areaCode}`,
              )}
            />

            <Section
              tone="info"
              icon={<CheckCircle2 className="h-4 w-4" />}
              title="Already active"
              blurb="You already have a number for these"
              count={existed.length}
              items={existed.map((e) => `area code ${e.areaCode}`)}
            />

            <Section
              tone="warn"
              icon={<PhoneOff className="h-4 w-4" />}
              title="Skipped — subscription cancelled"
              blurb="Not re-purchased, because these are no longer being paid for"
              count={skipped.length}
              items={skipped.map((s) => `area code ${s.areaCode}`)}
            />

            <Section
              tone="bad"
              icon={<AlertTriangle className="h-4 w-4" />}
              title="Needs attention"
              blurb="Could not be completed automatically"
              count={failed.length}
              defaultOpen
              items={failed.map((f) => `area code ${f.areaCode} — ${f.reason}`)}
            />

            {unbilled.length > 0 && (
              <div className="rounded-lg border border-blue-200 dark:border-blue-900">
                <div className="flex items-start gap-3 px-3 py-2.5">
                  <PhoneOff className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
                  <div className="flex-1">
                    <p className="text-sm font-medium">
                      {unbilled.length} number{unbilled.length === 1 ? "" : "s"} you&rsquo;re
                      using {unbilled.length === 1 ? "isn\u2019t" : "aren\u2019t"} on your plan
                    </p>
                    <p className="mt-0.5 text-xs text-gray-500 dark:text-gray-400">
                      Add them to keep using them. You keep the same number — nothing
                      is replaced.
                    </p>
                  </div>
                </div>
                <div className="max-h-56 overflow-y-auto overscroll-contain border-t border-gray-100 px-3 py-2 dark:border-gray-800">
                  {unbilled.map((n) => (
                    <div
                      key={n.id}
                      className="flex items-center justify-between gap-3 py-1.5"
                    >
                      <span className="font-mono text-xs text-gray-600 dark:text-gray-300">
                        {formatPhoneNumberDisplay(n.phoneNumber)}
                        <span className="ml-2 text-gray-400">area code {n.areaCode}</span>
                      </span>
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={busyId === n.id}
                        onClick={() => keepNumber(n)}
                      >
                        {busyId === n.id ? "Opening…" : "Keep — $2/mo"}
                      </Button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {skipped.length > 0 && (
              <p className="flex gap-2 rounded-lg bg-gray-50 p-2.5 text-xs text-gray-600 dark:bg-gray-900 dark:text-gray-400">
                <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                <span>
                  Skipped area codes are ones whose subscription has been
                  cancelled. Pressing this again will not buy them — resubscribe
                  first if you want them back.
                </span>
              </p>
            )}

            {data?.summary && (
              <p className="border-t pt-2 text-xs text-gray-400 dark:border-gray-800">
                {data.summary}
              </p>
            )}
          </div>
        )}

        <div className="flex justify-end pt-1">
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
